import { z } from "zod";

export const payPeriods = ["yearly", "monthly", "semimonthly", "biweekly", "weekly", "daily", "hourly"] as const;
export type PayPeriod = typeof payPeriods[number];

export const payPeriodLabels: Record<PayPeriod, string> = {
  yearly: "Year",
  monthly: "Month",
  semimonthly: "Semimonthly",
  biweekly: "Biweekly",
  weekly: "Week",
  daily: "Day",
  hourly: "Hour"
};

export const payPeriodMultipliers: Record<PayPeriod, number> = {
  yearly: 1,
  monthly: 12,
  semimonthly: 24,
  biweekly: 26,
  weekly: 52,
  daily: 260,
  hourly: 2080
};

export const countries = [
  { code: "uk", name: "United Kingdom", currency: "GBP", symbol: "£", flag: "🇬🇧" },
  { code: "us", name: "United States", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "ca", name: "Canada", currency: "CAD", symbol: "C$", flag: "🇨🇦" },
  { code: "au", name: "Australia", currency: "AUD", symbol: "A$", flag: "🇦🇺" },
  { code: "de", name: "Germany", currency: "EUR", symbol: "€", flag: "🇩🇪" },
  { code: "nl", name: "Netherlands", currency: "EUR", symbol: "€", flag: "🇳🇱" },
  { code: "ae", name: "UAE", currency: "AED", symbol: "د.إ", flag: "🇦🇪" },
  { code: "sg", name: "Singapore", currency: "SGD", symbol: "S$", flag: "🇸🇬" },
  { code: "ch", name: "Switzerland", currency: "CHF", symbol: "CHF", flag: "🇨🇭" },
  { code: "ie", name: "Ireland", currency: "EUR", symbol: "€", flag: "🇮🇪" },
  { code: "fr", name: "France", currency: "EUR", symbol: "€", flag: "🇫🇷" },
  { code: "es", name: "Spain", currency: "EUR", symbol: "€", flag: "🇪🇸" },
  { code: "it", name: "Italy", currency: "EUR", symbol: "€", flag: "🇮🇹" },
  { code: "se", name: "Sweden", currency: "SEK", symbol: "SEK", flag: "🇸🇪" },
  { code: "no", name: "Norway", currency: "NOK", symbol: "NOK", flag: "🇳🇴" },
  { code: "dk", name: "Denmark", currency: "DKK", symbol: "DKK", flag: "🇩🇰" },
  { code: "nz", name: "New Zealand", currency: "NZD", symbol: "NZ$", flag: "🇳🇿" },
  { code: "jp", name: "Japan", currency: "JPY", symbol: "¥", flag: "🇯🇵" },
  { code: "pt", name: "Portugal", currency: "EUR", symbol: "€", flag: "🇵🇹" },
  { code: "be", name: "Belgium", currency: "EUR", symbol: "€", flag: "🇧🇪" },
  { code: "at", name: "Austria", currency: "EUR", symbol: "€", flag: "🇦🇹" },
  { code: "pl", name: "Poland", currency: "PLN", symbol: "zł", flag: "🇵🇱" },
  { code: "hk", name: "Hong Kong", currency: "HKD", symbol: "HK$", flag: "🇭🇰" },
  { code: "kr", name: "South Korea", currency: "KRW", symbol: "₩", flag: "🇰🇷" },
  { code: "sa", name: "Saudi Arabia", currency: "SAR", symbol: "SAR", flag: "🇸🇦" },
  { code: "br", name: "Brazil", currency: "BRL", symbol: "R$", flag: "🇧🇷" },
  { code: "mx", name: "Mexico", currency: "MXN", symbol: "MX$", flag: "🇲🇽" },
  { code: "in", name: "India", currency: "INR", symbol: "₹", flag: "🇮🇳" },
  { code: "fi", name: "Finland", currency: "EUR", symbol: "€", flag: "🇫🇮" },
  { code: "cz", name: "Czech Republic", currency: "CZK", symbol: "Kč", flag: "🇨🇿" },
  { code: "hu", name: "Hungary", currency: "HUF", symbol: "Ft", flag: "🇭🇺" },
  { code: "gr", name: "Greece", currency: "EUR", symbol: "€", flag: "🇬🇷" },
  { code: "qa", name: "Qatar", currency: "QAR", symbol: "QAR", flag: "🇶🇦" },
  { code: "bh", name: "Bahrain", currency: "BHD", symbol: "BD", flag: "🇧🇭" },
  { code: "kw", name: "Kuwait", currency: "KWD", symbol: "KD", flag: "🇰🇼" },
  { code: "om", name: "Oman", currency: "OMR", symbol: "OMR", flag: "🇴🇲" },
  { code: "th", name: "Thailand", currency: "THB", symbol: "฿", flag: "🇹🇭" },
  { code: "my", name: "Malaysia", currency: "MYR", symbol: "RM", flag: "🇲🇾" },
  { code: "id", name: "Indonesia", currency: "IDR", symbol: "Rp", flag: "🇮🇩" },
  { code: "za", name: "South Africa", currency: "ZAR", symbol: "R", flag: "🇿🇦" },
  { code: "ng", name: "Nigeria", currency: "NGN", symbol: "₦", flag: "🇳🇬" },
  { code: "ke", name: "Kenya", currency: "KES", symbol: "KSh", flag: "🇰🇪" },
  { code: "il", name: "Israel", currency: "ILS", symbol: "₪", flag: "🇮🇱" },
  { code: "ru", name: "Russia", currency: "RUB", symbol: "₽", flag: "🇷🇺" },
  { code: "tr", name: "Turkey", currency: "TRY", symbol: "₺", flag: "🇹🇷" },
  // US States
  { code: "us-alabama", name: "Alabama", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-alaska", name: "Alaska", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-arizona", name: "Arizona", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-arkansas", name: "Arkansas", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-california", name: "California", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-colorado", name: "Colorado", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-connecticut", name: "Connecticut", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-delaware", name: "Delaware", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-district-of-columbia", name: "District of Columbia", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-florida", name: "Florida", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-georgia", name: "Georgia", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-hawaii", name: "Hawaii", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-idaho", name: "Idaho", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-illinois", name: "Illinois", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-indiana", name: "Indiana", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-iowa", name: "Iowa", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-kansas", name: "Kansas", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-kentucky", name: "Kentucky", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-louisiana", name: "Louisiana", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-maine", name: "Maine", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-maryland", name: "Maryland", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-massachusetts", name: "Massachusetts", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-michigan", name: "Michigan", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-minnesota", name: "Minnesota", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-mississippi", name: "Mississippi", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-missouri", name: "Missouri", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-montana", name: "Montana", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-nebraska", name: "Nebraska", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-nevada", name: "Nevada", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-new-hampshire", name: "New Hampshire", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-new-jersey", name: "New Jersey", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-new-mexico", name: "New Mexico", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-new-york", name: "New York", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-north-carolina", name: "North Carolina", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-north-dakota", name: "North Dakota", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-ohio", name: "Ohio", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-oklahoma", name: "Oklahoma", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-oregon", name: "Oregon", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-pennsylvania", name: "Pennsylvania", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-rhode-island", name: "Rhode Island", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-south-carolina", name: "South Carolina", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-south-dakota", name: "South Dakota", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-tennessee", name: "Tennessee", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-texas", name: "Texas", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-utah", name: "Utah", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-vermont", name: "Vermont", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-virginia", name: "Virginia", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-washington", name: "Washington", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-west-virginia", name: "West Virginia", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-wisconsin", name: "Wisconsin", currency: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "us-wyoming", name: "Wyoming", currency: "USD", symbol: "$", flag: "🇺🇸" },
] as const;

/**
 * A country name as it reads inside the phrase "in <country>".
 *
 * Four of our 96 markets take a definite article in English — "in the
 * Netherlands", "in the UAE", "in the United Kingdom", "in the United States",
 * "in the Czech Republic" — and the templates wrote "in Netherlands" and
 * "in UAE" on every one of those pages. That is visible in the SERP title and
 * description, which is the first thing a searcher reads, and it reads like
 * machine output. Use this wherever a name follows "in"; use the bare name (or
 * `name`'s possessive) everywhere else, because "the Netherlands's rules" is
 * wrong.
 */
const DEFINITE_ARTICLE = new Set(["uk", "us", "nl", "cz", "ae"]);
const ARTICLE_NAMES = new Set(["United Kingdom", "United States", "Netherlands", "UAE", "Czech Republic"]);

/**
 * Same rule as countryPhrase(), for callers that only hold the name (the deep
 * page facts carry a countryName, not a code). A US state page passes
 * "California, United States", which is not in the set, so it stays as-is —
 * "in California, United States" is already correct.
 */
export function placePhrase(name: string): string {
  return ARTICLE_NAMES.has(name) ? `the ${name}` : name;
}

export function countryPhrase(code: string, name: string): string {
  return DEFINITE_ARTICLE.has(code) ? `the ${name}` : placePhrase(name);
}

export type CountryCode = typeof countries[number]["code"];

export interface Country {
  code: CountryCode;
  name: string;
  currency: string;
  symbol: string;
  flag: string;
}

export interface TaxBreakdownItem {
  name: string;
  amount: number;
  percentage: number;
  color: string;
}

export interface TaxCalculationResult {
  grossSalary: number;
  netSalary: number;
  totalTax: number;
  effectiveTaxRate: number;
  breakdown: TaxBreakdownItem[];
  country: Country;
  payPeriod: PayPeriod;
}

export interface USState {
  code: string;
  name: string;
  taxRate: number;
}

export const usStates: USState[] = [
  { code: "AL", name: "Alabama", taxRate: 5.00 },
  { code: "AK", name: "Alaska", taxRate: 0.00 },
  { code: "AZ", name: "Arizona", taxRate: 2.50 },
  { code: "AR", name: "Arkansas", taxRate: 4.40 },
  { code: "CA", name: "California", taxRate: 9.30 },
  { code: "CO", name: "Colorado", taxRate: 4.40 },
  { code: "CT", name: "Connecticut", taxRate: 5.00 },
  { code: "DE", name: "Delaware", taxRate: 6.60 },
  { code: "DC", name: "District of Columbia", taxRate: 8.50 },
  { code: "FL", name: "Florida", taxRate: 0.00 },
  { code: "GA", name: "Georgia", taxRate: 5.49 },
  { code: "HI", name: "Hawaii", taxRate: 8.25 },
  { code: "ID", name: "Idaho", taxRate: 5.80 },
  { code: "IL", name: "Illinois", taxRate: 4.95 },
  { code: "IN", name: "Indiana", taxRate: 3.05 },
  { code: "IA", name: "Iowa", taxRate: 5.70 },
  { code: "KS", name: "Kansas", taxRate: 5.70 },
  { code: "KY", name: "Kentucky", taxRate: 4.00 },
  { code: "LA", name: "Louisiana", taxRate: 4.25 },
  { code: "ME", name: "Maine", taxRate: 7.15 },
  { code: "MD", name: "Maryland", taxRate: 5.75 },
  { code: "MA", name: "Massachusetts", taxRate: 5.00 },
  { code: "MI", name: "Michigan", taxRate: 4.25 },
  { code: "MN", name: "Minnesota", taxRate: 7.85 },
  { code: "MS", name: "Mississippi", taxRate: 5.00 },
  { code: "MO", name: "Missouri", taxRate: 4.80 },
  { code: "MT", name: "Montana", taxRate: 5.90 },
  { code: "NE", name: "Nebraska", taxRate: 5.84 },
  { code: "NV", name: "Nevada", taxRate: 0.00 },
  { code: "NH", name: "New Hampshire", taxRate: 0.00 },
  { code: "NJ", name: "New Jersey", taxRate: 6.37 },
  { code: "NM", name: "New Mexico", taxRate: 4.90 },
  { code: "NY", name: "New York", taxRate: 6.85 },
  { code: "NC", name: "North Carolina", taxRate: 4.75 },
  { code: "ND", name: "North Dakota", taxRate: 2.50 },
  { code: "OH", name: "Ohio", taxRate: 3.50 },
  { code: "OK", name: "Oklahoma", taxRate: 4.75 },
  { code: "OR", name: "Oregon", taxRate: 9.00 },
  { code: "PA", name: "Pennsylvania", taxRate: 3.07 },
  { code: "RI", name: "Rhode Island", taxRate: 5.99 },
  { code: "SC", name: "South Carolina", taxRate: 6.40 },
  { code: "SD", name: "South Dakota", taxRate: 0.00 },
  { code: "TN", name: "Tennessee", taxRate: 0.00 },
  { code: "TX", name: "Texas", taxRate: 0.00 },
  { code: "UT", name: "Utah", taxRate: 4.65 },
  { code: "VT", name: "Vermont", taxRate: 6.60 },
  { code: "VA", name: "Virginia", taxRate: 5.75 },
  { code: "WA", name: "Washington", taxRate: 0.00 },
  { code: "WV", name: "West Virginia", taxRate: 5.12 },
  { code: "WI", name: "Wisconsin", taxRate: 5.30 },
  { code: "WY", name: "Wyoming", taxRate: 0.00 }
];

export const calculateSalarySchema = z.object({
  salary: z.number().positive(),
  payPeriod: z.enum(payPeriods),
  countryCode: z.string(),
  stateCode: z.string().optional()
});

export type CalculateSalaryInput = z.infer<typeof calculateSalarySchema>;

export interface CountryComparisonResult {
  country: Country;
  grossSalary: number;
  netSalary: number;
  totalTax: number;
  effectiveTaxRate: number;
  difference: number;
}
